// =============================================================================
// File: frontend/src/components/TwinOverview.jsx
// Digital Twin Overview Tab — Status, Alerts, Baselines, Score Projection
// Patent claims exercised: P-011 C1, C4, C12, C13
// =============================================================================

import React from 'react';
import { API_BASE } from '../config';

// Domain labels and icons
const DOMAIN_INFO = {
  biometric: { label: 'Biometric', icon: '❤️' },
  nutritional: { label: 'Nutritional', icon: '🥗' },
  behavioral: { label: 'Behavioral', icon: '🏃' },
  pharmaceutical: { label: 'Pharmaceutical', icon: '💊' },
};

// Severity color mapping
const SEVERITY_CLASS = {
  critical: 'dt-severity-critical',
  significant: 'dt-severity-significant',
  moderate: 'dt-severity-moderate',
  minor: 'dt-severity-minor',
};

export default function TwinOverview({ twinState, loading, syncing, syncResult, fetchWithAuth, onRefresh, onSync }) {
  if (loading && !twinState) {
    return (
      <div className="dt-loading">
        <div className="dt-spinner"></div>
        <p>Loading digital twin state...</p>
      </div>
    );
  }

  if (!twinState && !syncing) {
    return (
      <div className="dt-empty">
        <h3>No Twin Data Yet</h3>
        <p>Your digital twin will be built as health data flows in from your connected devices, nutrition logs, and medical records.</p>
        <button className="btn btn-primary" onClick={onSync} disabled={syncing}>
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>
    );
  }

  const {
    baselines, active_anomalies, data_freshness,
    active_pattern_count, total_metrics_tracked, last_sync_at
  } = twinState || {};

  // Group baselines by domain
  const baselinesByDomain = {};
  if (baselines) {
    Object.entries(baselines).forEach(([key, windows]) => {
      const [domain, metric] = key.split('.');
      if (!baselinesByDomain[domain]) baselinesByDomain[domain] = [];
      const w14 = windows['14d'];
      if (w14) {
        baselinesByDomain[domain].push({
          metric,
          mean: w14.mean,
          stddev: w14.stddev,
          sample_count: w14.sample_count,
        });
      }
    });
  }

  const handleDismiss = async (anomalyId) => {
    try {
      await fetchWithAuth(`${API_BASE}/api/twin/anomalies/${anomalyId}/dismiss`, { method: 'POST' });
      onRefresh();
    } catch (err) {
      console.error('Failed to dismiss anomaly:', err);
    }
  };

  return (
    <div className="dt-overview">
      {/* Sync Status Banner */}
      {syncing && (
        <div className="dt-syncBanner dt-syncBanner-active">
          <div className="dt-spinner-sm"></div>
          <span>Syncing health data and computing baselines...</span>
        </div>
      )}
      {!syncing && syncResult && !syncResult.error && (
        <div className="dt-syncBanner dt-syncBanner-success">
          <span>
            Sync complete: {syncResult.metrics_synced || 0} data points ingested,
            {' '}{syncResult.baselines_computed || 0} baselines computed
          </span>
          {syncResult.errors?.length > 0 && (
            <span className="dt-syncBanner-warnings">
              {' '}({syncResult.errors.length} source{syncResult.errors.length > 1 ? 's' : ''} unavailable)
            </span>
          )}
        </div>
      )}
      {!syncing && syncResult?.error && (
        <div className="dt-syncBanner dt-syncBanner-error">
          <span>{syncResult.error}</span>
        </div>
      )}

      {/* Twin Status Card */}
      <div className="dt-statusCard">
        <div className="dt-statusCard-header">
          <h3>Twin Status</h3>
          <button
            className="dt-syncBtn"
            onClick={onSync}
            disabled={syncing}
            title="Force sync all health data"
          >
            {syncing ? '⟳ Syncing...' : '↻ Sync'}
          </button>
        </div>
        <div className="dt-statusCard-grid">
          <div className="dt-stat">
            <span className="dt-stat-value">{total_metrics_tracked || 0}</span>
            <span className="dt-stat-label">Metrics Tracked</span>
          </div>
          <div className="dt-stat">
            <span className="dt-stat-value">{active_pattern_count || 0}</span>
            <span className="dt-stat-label">Active Patterns</span>
          </div>
          <div className="dt-stat">
            <span className="dt-stat-value">{active_anomalies?.length || 0}</span>
            <span className="dt-stat-label">Active Alerts</span>
          </div>
          <div className="dt-stat">
            <span className="dt-stat-value">
              {last_sync_at ? new Date(last_sync_at).toLocaleTimeString() : '—'}
            </span>
            <span className="dt-stat-label">Last Sync</span>
          </div>
        </div>

        {/* Data Freshness */}
        {data_freshness && Object.keys(data_freshness).length > 0 && (
          <div className="dt-freshness">
            {Object.entries(data_freshness).map(([domain, info]) => (
              <span key={domain} className={`dt-freshness-badge dt-freshness-${info.status}`}>
                {DOMAIN_INFO[domain]?.icon || '📊'} {DOMAIN_INFO[domain]?.label || domain}
                <span className="dt-freshness-age">
                  {info.age_hours != null ? `${Math.round(info.age_hours)}h ago` : 'No data'}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Readings outside the user's usual range (H5, 2026-11-02) */}
      {active_anomalies && active_anomalies.length > 0 && (
        <div className="dt-alertsSection">
          <h3>Readings outside your usual range</h3>
          <div className="dt-alertsList">
            {active_anomalies.map(anomaly => {
              // The backend's one severity definition (|z| vs. the personal baseline) — never derived
              // here. The internal key only picks the CSS class; the user sees `severity_label`.
              const severity = SEVERITY_CLASS[anomaly.severity] ? anomaly.severity : 'minor';
              const sds = Math.abs(Number(anomaly.z_score) || 0).toFixed(1);

              return (
                <div key={anomaly.id} className={`dt-alertCard ${SEVERITY_CLASS[severity]}`}>
                  <div className="dt-alertCard-header">
                    <span className="dt-alertCard-metric">
                      {anomaly.metric_label || anomaly.metric_name.replace(/_/g, ' ')}
                    </span>
                    <span className="dt-alertCard-badge">{anomaly.severity_label || 'Outside your usual range'}</span>
                  </div>
                  {anomaly.alert_narrative && (
                    <p className="dt-alertCard-narrative">{anomaly.alert_narrative}</p>
                  )}
                  <div className="dt-alertCard-stats">
                    <span>Your reading: {anomaly.observed_value}</span>
                    <span>Your 14-day average: {anomaly.baseline_mean}</span>
                    <span>{sds} standard deviations from your average</span>
                  </div>
                  <div className="dt-alertCard-actions">
                    <button
                      className="dt-dismissBtn"
                      onClick={() => handleDismiss(anomaly.id)}
                    >
                      Dismiss
                    </button>
                    <span className="dt-alertCard-time">
                      {new Date(anomaly.detected_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="dt-alertsFootnote">
            These compare each reading with your own recent history. They are not a diagnosis, are
            not real-time monitoring, and are not for emergencies — if you feel unwell, contact a
            healthcare professional or emergency services.
          </p>
        </div>
      )}

      {/* Rolling Baselines Grid */}
      <div className="dt-baselinesSection">
        <h3>Rolling Baselines (14-day)</h3>
        {Object.keys(baselinesByDomain).length === 0 ? (
          <p className="dt-empty-text">
            {syncing
              ? 'Computing baselines from your health data...'
              : 'No baselines computed yet. Your twin syncs automatically when you visit this page.'
            }
          </p>
        ) : (
          <div className="dt-baselinesGrid">
            {Object.entries(baselinesByDomain).map(([domain, metrics]) => (
              <div key={domain} className="dt-domainGroup">
                <h4 className="dt-domainGroup-title">
                  {DOMAIN_INFO[domain]?.icon || '📊'} {DOMAIN_INFO[domain]?.label || domain}
                </h4>
                <div className="dt-metricsTable">
                  <div className="dt-metricsTable-header">
                    <span>Metric</span>
                    <span>Mean ± StdDev</span>
                    <span>Samples</span>
                  </div>
                  {metrics.map(m => (
                    <div key={m.metric} className="dt-metricsTable-row">
                      <span className="dt-metricName">{m.metric.replace(/_/g, ' ')}</span>
                      <span className="dt-metricValue">
                        {m.mean.toFixed(1)} ± {m.stddev.toFixed(1)}
                      </span>
                      <span className="dt-metricSamples">{m.sample_count}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
